// ─────────────────────────────────────────────────────────
//  Islamic & Goal Icons — Custom SVG icon set for Ehille
//  Sources: SVG Repo (CC0), custom designs
// ─────────────────────────────────────────────────────────

import { cn } from "@/lib/utils";

interface IconProps {
  size?: number;
  className?: string;
}

// ── Mosque / Cami ────────────────────────────────────────
export function MosqueIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      <path d="M12 2C9.5 2 7 4.5 7 7v2H5v2H3v11h8v-5a1 1 0 012 0v5h8V11h-2V9h-2V7c0-2.5-2.5-5-5-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 2v3M9 7h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Minaret */}
      <path d="M4 11v-3a1 1 0 012 0v3M18 11v-3a1 1 0 012 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Crescent on top */}
      <circle cx="12" cy="3" r="1" fill="currentColor"/>
    </svg>
  );
}

// ── Quran / Kuran ────────────────────────────────────────
export function QuranIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Star decoration */}
      <path d="M12 7l1 2.5h2.5l-2 1.5.8 2.5L12 12l-2.3 1.5.8-2.5-2-1.5H11L12 7z" fill="currentColor" opacity="0.6"/>
    </svg>
  );
}

// ── Prayer / Dua / Namaz ─────────────────────────────────
export function PrayerIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      {/* Person in sujud/prayer */}
      <circle cx="17" cy="5" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 20h4l2-4 3-3v-3l-4 3-3 1H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13 16l-2 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Tasbih / Tesbih ──────────────────────────────────────
export function TasbihIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"/>
      <circle cx="12" cy="4" r="1.5" fill="currentColor"/>
      <circle cx="18" cy="8" r="1.5" fill="currentColor"/>
      <circle cx="18" cy="16" r="1.5" fill="currentColor"/>
      <circle cx="12" cy="20" r="1.5" fill="currentColor"/>
      <circle cx="6" cy="16" r="1.5" fill="currentColor"/>
      <circle cx="6" cy="8" r="1.5" fill="currentColor"/>
      <path d="M12 4v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Crescent / Hilal ─────────────────────────────────────
export function CrescentIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Sadaqa / Charity ─────────────────────────────────────
export function SadaqaIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.15"/>
      <path d="M12 14l-1-2h2l-1 2z" fill="currentColor"/>
    </svg>
  );
}

// ── Podcast ──────────────────────────────────────────────
export function PodcastIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={cn("", className)}>
      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
      <circle cx="12" cy="9" r="2.5"/>
      <path d="M8 14a4.5 4.5 0 018 0"/>
      <path d="M6 11a6.5 6.5 0 0112 0"/>
    </svg>
  );
}

// ── YouTube ──────────────────────────────────────────────
export function YouTubeIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      <rect x="2" y="4" width="20" height="16" rx="4" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 8.5v7l5.5-3.5L10 8.5z" fill="currentColor"/>
    </svg>
  );
}

// ── Tutorial / Course ────────────────────────────────────
export function TutorialIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={cn("", className)}>
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
      <path d="M6 8h2M6 12h2M16 8h2M16 12h2"/>
    </svg>
  );
}

// ── Water / Su ───────────────────────────────────────────
export function WaterIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={cn("", className)}>
      <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0L12 2.69z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Sport / Exercise ─────────────────────────────────────
export function SportIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={cn("", className)}>
      <circle cx="12" cy="5" r="2"/>
      <path d="M7 21l3-7 2 2 2-2 3 7"/>
      <path d="M5 12h14"/>
      <path d="M9 12l-2-4M15 12l2-4"/>
    </svg>
  );
}

// ── Book / Kitap ─────────────────────────────────────────
export function BookIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={cn("", className)}>
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
    </svg>
  );
}

// ── Study / Ilim ─────────────────────────────────────────
export function StudyIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={cn("", className)}>
      <path d="M22 10v6M2 10l10-5 10 5-10 5-10-5z"/>
      <path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  );
}

// ── Icon Map — maps icon IDs to components ───────────────
export const ICON_MAP: Record<string, React.FC<IconProps>> = {
  mosque: MosqueIcon,
  quran: QuranIcon,
  prayer: PrayerIcon,
  tasbih: TasbihIcon,
  crescent: CrescentIcon,
  sadaqa: SadaqaIcon,
  podcast: PodcastIcon,
  youtube: YouTubeIcon,
  tutorial: TutorialIcon,
  water: WaterIcon,
  sport: SportIcon,
  book: BookIcon,
  study: StudyIcon,
};

// ── Goal icon picker list ────────────────────────────────
export const GOAL_ICONS = [
  { id: "mosque", label: "Cami", category: "ibadet" },
  { id: "quran", label: "Kuran", category: "ibadet" },
  { id: "prayer", label: "Namaz", category: "ibadet" },
  { id: "tasbih", label: "Tesbih", category: "ibadet" },
  { id: "crescent", label: "Hilal", category: "ibadet" },
  { id: "sadaqa", label: "Sadaka", category: "ibadet" },
  { id: "book", label: "Kitap", category: "egitim" },
  { id: "study", label: "Ilim", category: "egitim" },
  { id: "tutorial", label: "Ders", category: "egitim" },
  { id: "podcast", label: "Podcast", category: "egitim" },
  { id: "youtube", label: "Video", category: "egitim" },
  { id: "sport", label: "Spor", category: "saglik" },
  { id: "water", label: "Su", category: "saglik" },
];
